import { CourseOption as NewCourseOption, Meeting as NewMeeting } from '../types/course.response';
import { parseTimeToMinutes } from './timeUtils';

const DAY_MAP: Record<string, number> = { M: 0, T: 1, W: 2, R: 3, F: 4, S: 5, U: 6 };

export const parseDayPattern = (pattern: string): number[] => {
  return Array.from(pattern)
    .map(ch => DAY_MAP[ch])
    .filter((idx): idx is number => idx !== undefined);
};

export const hasMeetingConflict = (a: NewMeeting, b: NewMeeting): boolean => {
  const aDays = parseDayPattern(a.pattern ?? "");
  const bDays = parseDayPattern(b.pattern ?? "");
  const sharedDay = aDays.some(d => bDays.includes(d));
  if (!sharedDay) return false;

  const aStart = parseTimeToMinutes(a.timeStart ?? "");
  const aEnd   = parseTimeToMinutes(a.timeEnd ?? "");
  const bStart = parseTimeToMinutes(b.timeStart ?? "");
  const bEnd   = parseTimeToMinutes(b.timeEnd ?? "");
  if (aStart == null || aEnd == null || bStart == null || bEnd == null) return false;

  return aStart < bEnd && bStart < aEnd;
};

export const collectScheduledMeetings = (
  existingCourses: NewCourseOption[],
  semester: string,
): NewMeeting[] => {
  const meetings: NewMeeting[] = [];
  for (const course of existingCourses) {
    if (course.userState.semester !== semester) continue;
    const selectedNumbers = new Set(course.userState.sectionNumbers);

    for (const eg of course.enrollGroups) {
      if (eg.semester !== semester) continue;
      for (const cs of eg.classSections) {
        if (selectedNumbers.has(cs.sectionNumber)) {
          meetings.push(...cs.meetings);
        }
      }
    }
  }
  return meetings;
};

/**
 * Map ClassSection.id values back to sectionNumber strings for a given course/semester.
 */
export const classSectionIdsToNumbers = (
  course: NewCourseOption,
  semester: string,
  ids: number[],
): string[] => {
  const idSet = new Set(ids);
  return course.enrollGroups
    .filter(eg => eg.semester === semester)
    .flatMap(eg => eg.classSections)
    .filter(cs => idSet.has(cs.id))
    .map(cs => cs.sectionNumber);
};

/**
 * Auto-select one section per required sectionType, preferring conflict-free picks.
 * Falls back to the first section of each type when no conflict-free set exists.
 * Returns ClassSection.id values (not sectionNumbers).
 */
export const autoAssignSections = (
  course: NewCourseOption,
  semester: string | null,
  existingCourses: NewCourseOption[],
): number[] => {
  if (semester === null) return [];
  const candidateGroups = course.enrollGroups.filter(eg => eg.semester === semester);
  if (candidateGroups.length === 0) return [];

  const occupied = collectScheduledMeetings(existingCourses, semester);

  const sectionConflicts = (cs: { meetings: NewMeeting[] }): boolean =>
    cs.meetings.some(m => occupied.some(o => hasMeetingConflict(m, o)));

  for (const eg of candidateGroups) {
    const requiredTypes = Array.from(new Set(eg.classSections.map(cs => cs.sectionType)));
    const picks: number[] = [];
    let allSatisfied = true;

    for (const type of requiredTypes) {
      const candidates = eg.classSections.filter(cs => cs.sectionType === type);
      const pick = candidates.find(cs => !sectionConflicts(cs));
      if (pick) {
        picks.push(pick.id);
      } else {
        allSatisfied = false;
        break;
      }
    }

    if (allSatisfied) return picks;
  }

  // Fallback: first section of each type from the first candidate group
  const fallbackGroup = candidateGroups[0];
  const requiredTypes = Array.from(new Set(fallbackGroup.classSections.map(cs => cs.sectionType)));
  return requiredTypes.map(type => {
    const first = fallbackGroup.classSections.find(cs => cs.sectionType === type);
    return first!.id;
  });
};
